/* global React, Icon, Button, Field, Input, Select, Card, EmptyState, Modal, ConfirmModal, PlummeI18n, SupportMessageComposer, SupportMessageContent, useSupportTicketDelete, emptySupportMessageDraft, supportMessageDraftHasContent, t, toast */
(function (global) {
  const SUPPORT_CATEGORIES = ['feil', 'fakturering', 'sporsmal', 'annet'];
  const supportCategoryLabel = (value) => t(`support.category.${SUPPORT_CATEGORIES.includes(value) ? value : 'annet'}`);
  const supportTicketIsOpen = (ticket) => !['Solved', 'Closed'].includes(String(ticket?.status || 'New'));
  const supportDate = (value) => PlummeI18n.date(value, { day: '2-digit', month: 'short', year: 'numeric' });
  const supportDateOrDash = (value) => (Number.isFinite(Date.parse(value)) ? supportDate(value) : '\u2013');
  const supportDateTime = (value) => PlummeI18n.dateTime(value, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  const supportError = (key) => t(`support.error.${key}`);

  function SupportTicketForm({ formId, onSubmit, busy, error, submitLabel, allowScreenshot = false, initialCategory = 'feil', initialSubject = '', initialMessage = '' }) {
    const [category, setCategory] = React.useState(initialCategory);
    const [subject, setSubject] = React.useState(initialSubject);
    const [message, setMessage] = React.useState(() => (initialMessage ? { ...emptySupportMessageDraft(), body: initialMessage } : emptySupportMessageDraft()));
    const [captureBusy, setCaptureBusy] = React.useState(false);
    const [validationError, setValidationError] = React.useState('');
    const submit = (event) => {
      event.preventDefault();
      if (busy || message.optimizing || captureBusy) return;
      if (!subject.trim() || !supportMessageDraftHasContent(message)) {
        setValidationError(supportError('required'));
        return;
      }
      setValidationError('');
      onSubmit({ category, subject: subject.trim(), message });
    };
    return <form id={formId} className="support-ticket-form" onSubmit={submit}>
      <Field label={t('support.category')} help={t('helpTopics.support.body')} helpTitle={t('helpTopics.support.title')}>
        <Select value={category} onChange={(event) => setCategory(event.target.value)} disabled={busy}>
          {SUPPORT_CATEGORIES.map((value) => <option key={value} value={value}>{supportCategoryLabel(value)}</option>)}
        </Select>
      </Field>
      <Field label={t('support.subject')} help={t('helpTopics.support.body')} helpTitle={t('helpTopics.support.title')}>
        <Input autoFocus value={subject} onChange={(event) => { setSubject(event.target.value); setValidationError(''); }} placeholder={t('support.subject.placeholder')} maxLength={120} disabled={busy} />
      </Field>
      <Field label={t('support.message')} help={t('helpTopics.support.body')} helpTitle={t('helpTopics.support.title')}>
        <SupportMessageComposer value={message} onChange={(next) => { setMessage(next); setValidationError(''); }} placeholder={t('support.message.placeholder')} disabled={busy} allowScreenshot={allowScreenshot} onCaptureChange={setCaptureBusy} />
      </Field>
      {(error || validationError) && <div className="support-error" role="alert">{error || validationError}</div>}
      {!formId && <Button type="submit" className="btn-block" disabled={busy || message.optimizing || captureBusy || !subject.trim() || !supportMessageDraftHasContent(message)}>{busy ? t('support.sending') : submitLabel}</Button>}
    </form>;
  }

  function SupportFAB({ client, route }) {
    const [open, setOpen] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [sent, setSent] = React.useState(null);
    const [error, setError] = React.useState('');
    const [prefill, setPrefill] = React.useState(null);
    const [formKey, setFormKey] = React.useState(0);
    const triggerRef = React.useRef(null);
    const close = () => {
      if (busy) return;
      setOpen(false);
      setSent(null);
      setError('');
      window.setTimeout(() => triggerRef.current?.focus(), 0);
    };
    React.useEffect(() => {
      setOpen(false);
      setSent(null);
      setError('');
    }, [client]);
    React.useEffect(() => {
      window.openSupportFAB = ({ category, subject, message } = {}) => {
        setPrefill({ category, subject, message });
        setFormKey((value) => value + 1);
        setSent(null);
        setError('');
        setOpen(true);
      };
      return () => { delete window.openSupportFAB; };
    }, []);
    React.useEffect(() => {
      if (!open) return undefined;
      void window.PlummeBackend.warmSupportService?.();
      const onKey = (event) => { if (event.key === 'Escape') close(); };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }, [open, busy]);
    const submit = async (input) => {
      setBusy(true);
      setError('');
      try {
        const result = await window.PlummeBackend.createSupportTicket({ ...input, sourceRoute: route });
        setSent(result.ticket);
      } catch (requestError) {
        setError(supportError('create'));
      } finally {
        setBusy(false);
      }
    };
    return <div className={`support-fab-wrap ${open ? 'open' : ''}`} data-support-screenshot-ignore data-html2canvas-ignore="true">
      {open && <button className="support-fab-scrim" type="button" aria-label={t('support.close')} onClick={close}  data-help={t('helpTopics.supportClose.body')} data-help-title={t('helpTopics.supportClose.title')}/>}
      {open && <div className="card support-fab-pop" role="dialog" aria-modal="true" aria-labelledby="support-fab-title">
        {sent ? <div className="support-fab-success">
          <span className="support-fab-success-icon"><Icon name="checkCircle" size={26} /></span>
          <div className="support-fab-success-title">{t('support.sent.title')}</div>
          <div className="support-fab-success-text">{t('support.sent.text', { id: sent.id })}</div>
          <Button variant="secondary" className="btn-block" onClick={close} data-help={t('helpTopics.supportClose.body')} data-help-title={t('helpTopics.supportClose.title')}>{t('support.close')}</Button>
        </div> : <React.Fragment>
          <div className="support-fab-hd">
            <div id="support-fab-title" className="support-fab-title">{t('support.contact')}</div>
            <button type="button" className="icon-btn ghost sm" onClick={close} aria-label={t('support.close')} disabled={busy} data-help={t('helpTopics.supportClose.body')} data-help-title={t('helpTopics.supportClose.title')}><Icon name="x" size={16} /></button>
          </div>
          <SupportTicketForm key={formKey} onSubmit={submit} busy={busy} error={error} submitLabel={t('support.send')} allowScreenshot initialCategory={prefill?.category} initialSubject={prefill?.subject} initialMessage={prefill?.message} />
        </React.Fragment>}
      </div>}
      <button ref={triggerRef} type="button" className="support-fab-btn" onClick={() => setOpen((value) => !value)} aria-label={t('support.contact')} title={t('support.contact')} aria-expanded={open}>
        <Icon name={open ? 'x' : 'chat'} size={22} />
      </button>
    </div>;
  }

  function NewSupportTicketModal({ onClose, onCreate }) {
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');
    React.useEffect(() => { void window.PlummeBackend.warmSupportService?.(); }, []);
    const submit = async (input) => {
      setBusy(true);
      setError('');
      try {
        await onCreate(input);
      } catch (requestError) {
        setError(supportError('create'));
      } finally {
        setBusy(false);
      }
    };
    return <Modal open portal onClose={busy ? () => {} : onClose} title={t('support.new.title')} max={440} footer={
      <Button type="submit" form="support-new-ticket-form" className="btn-block" disabled={busy} data-help={t('helpTopics.support.body')} data-help-title={t('helpTopics.support.title')}>{busy ? t('support.sending') : t('support.send')}</Button>
    }>
      <SupportTicketForm formId="support-new-ticket-form" onSubmit={submit} busy={busy} error={error} submitLabel={t('support.send')} />
    </Modal>;
  }

  function SupportTicketDetail({ ticket, initialMessages = [], onBack, onTicketChange, onTicketDeleted }) {
    const hasInitialMessages = initialMessages.length > 0;
    const skipInitialLoadRef = React.useRef(hasInitialMessages);
    const [messages, setMessages] = React.useState(initialMessages);
    const [loading, setLoading] = React.useState(!hasInitialMessages);
    const [loadError, setLoadError] = React.useState('');
    const [draft, setDraft] = React.useState(() => emptySupportMessageDraft());
    const [replyBusy, setReplyBusy] = React.useState(false);
    const [statusBusy, setStatusBusy] = React.useState(false);
    const [confirmReopen, setConfirmReopen] = React.useState(false);
    const { requestDelete, deleteDialog, deleteBusy } = useSupportTicketDelete(onTicketDeleted);
    const isOpen = supportTicketIsOpen(ticket);
    const loadThread = React.useCallback(async (quiet = false) => {
      if (!quiet) setLoading(true);
      if (!quiet) setLoadError('');
      try {
        const result = await window.PlummeBackend.getSupportTicketThread(ticket.id);
        setMessages(result.messages);
        if (result.ticket) onTicketChange(result.ticket);
      } catch (requestError) {
        if (!quiet) setLoadError(supportError('thread'));
      } finally {
        if (!quiet) setLoading(false);
      }
    }, [ticket.id]);
    React.useEffect(() => {
      if (skipInitialLoadRef.current) {
        skipInitialLoadRef.current = false;
        return;
      }
      loadThread();
    }, [loadThread]);
    React.useEffect(() => {
      const onFocus = () => loadThread(true);
      window.addEventListener('focus', onFocus);
      return () => window.removeEventListener('focus', onFocus);
    }, [loadThread]);
    const sendReply = async (event) => {
      event.preventDefault();
      if (!supportMessageDraftHasContent(draft) || draft.optimizing || replyBusy) return;
      setReplyBusy(true);
      try {
        const result = await window.PlummeBackend.replyToSupportTicket(ticket.id, draft);
        setMessages((current) => [...current, result.message]);
        if (result.ticket) onTicketChange(result.ticket);
        setDraft(emptySupportMessageDraft());
      } catch (requestError) {
        toast(supportError('reply'), 'error');
      } finally {
        setReplyBusy(false);
      }
    };
    const changeStatus = async (status) => {
      setStatusBusy(true);
      try {
        const updated = await window.PlummeBackend.setSupportTicketStatus(ticket.id, status);
        if (updated) onTicketChange(updated);
      } catch (requestError) {
        toast(supportError('status'), 'error');
        throw requestError;
      } finally {
        setStatusBusy(false);
      }
    };
    return <div className="support-detail">
      {deleteDialog}
      {confirmReopen && <ConfirmModal open onClose={() => setConfirmReopen(false)} onConfirm={() => changeStatus('Open')}
        title={t('support.reopen.title')} confirmLabel={t('support.reopen.confirm')} danger={false} busy={statusBusy}>
        {t('support.reopen.text')}
      </ConfirmModal>}
      <div className="support-detail-hd">
        <button type="button" className="icon-btn ghost" onClick={onBack} aria-label={t('support.back')}><Icon name="arrowLeft" size={18} /></button>
        <div className="grow support-detail-copy">
          <div className="support-detail-subject">{ticket.subject}</div>
          <div className="t-sm muted">{ticket.id} · {supportCategoryLabel(ticket.category)} · {t('support.opened', { date: supportDateTime(ticket.createdAt) })}</div>
        </div>
        <span className={`badge ${isOpen ? 'badge-info' : 'badge-success'}`}>{isOpen ? t('support.status.open') : t('support.status.closed')}</span>
        <Button variant={isOpen ? 'secondary' : 'ghost'} size="sm" disabled={statusBusy} onClick={() => isOpen ? changeStatus('Closed') : setConfirmReopen(true)}>
          {statusBusy ? t('common.working') : isOpen ? t('support.closeTicket') : t('support.reopen.action')}
        </Button>
        <Button variant="danger" size="sm" icon="trash" className="support-delete-btn" disabled={statusBusy || deleteBusy} onClick={() => requestDelete(ticket)}>{t('support.delete.action')}</Button>
      </div>
      <Card>
        {loading ? <div className="support-state"><span className="spinner" />{t('support.loading.thread')}</div>
          : loadError ? <div className="support-state support-state-error" role="alert"><span>{loadError}</span><Button size="sm" variant="secondary" onClick={loadThread}>{t('support.retry')}</Button></div>
          : <div className="support-thread" aria-live="polite">
            {messages.map((message) => <div key={message.id} className={`support-bubble ${message.role === 'Customer' ? 'user' : 'agent'}`}>
              <div className="support-bubble-from">{message.role === 'Customer' ? t('support.you') : t('support.agent')}</div>
              <SupportMessageContent message={message} />
              <div className="support-bubble-time">{supportDateTime(message.createdAt)}</div>
            </div>)}
          </div>}
        {isOpen ? <form className="support-compose" onSubmit={sendReply}>
          <SupportMessageComposer value={draft} onChange={setDraft} placeholder={t('support.reply.placeholder')} disabled={replyBusy || loading || Boolean(loadError)} />
          <Button type="submit" disabled={!supportMessageDraftHasContent(draft) || draft.optimizing || replyBusy || loading || Boolean(loadError)} data-help={t('helpTopics.support.body')} data-help-title={t('helpTopics.support.title')}>{replyBusy ? t('support.sending') : draft.optimizing ? t('support.editor.optimizing') : t('support.send')}</Button>
        </form> : <div className="support-closed-note"><Icon name="checkCircle" size={16} />{t('support.closed.note')}</div>}
      </Card>
    </div>;
  }

  function SupportTicketsView({ route, client, setSubPage }) {
    const [tickets, setTickets] = React.useState([]);
    const [filter, setFilter] = React.useState('all');
    const [activeId, setActiveId] = React.useState(null);
    const [initialMessagesByTicket, setInitialMessagesByTicket] = React.useState({});
    const [showNew, setShowNew] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const loadTickets = React.useCallback(async (quiet = false) => {
      if (!quiet) setLoading(true);
      setError('');
      try {
        setTickets(await window.PlummeBackend.listSupportTickets());
      } catch (requestError) {
        setError(supportError('list'));
      } finally {
        if (!quiet) setLoading(false);
      }
    }, [client]);
    React.useEffect(() => {
      setActiveId(null);
      loadTickets();
    }, [loadTickets]);
    // The support-reply email links to /ovrig/support?sak=PLM-…
    // (functions/support/supportReplyNotification.ts). Open that case once it
    // is in the customer's own list; an ID that is not theirs just shows the list.
    const linkedTicketId = React.useRef('');
    if (linkedTicketId.current === '') {
      let requested = '';
      try { requested = new URLSearchParams(window.location.search).get('sak') || ''; } catch (error) { requested = ''; }
      linkedTicketId.current = /^PLM-\d+$/.test(requested) ? requested : null;
    }
    React.useEffect(() => {
      const id = linkedTicketId.current;
      if (!id || !tickets.some((ticket) => ticket.id === id)) return;
      linkedTicketId.current = null;
      setActiveId(id);
    }, [tickets]);
    const removeTicket = React.useCallback((ticketId) => {
      setTickets((current) => current.filter((ticket) => ticket.id !== ticketId));
      setInitialMessagesByTicket((current) => {
        if (!(ticketId in current)) return current;
        const next = { ...current };
        delete next[ticketId];
        return next;
      });
    }, []);
    React.useEffect(() => {
      const refresh = (event) => {
        const updated = event?.detail;
        if (updated?.archivedTicketId) {
          removeTicket(updated.archivedTicketId);
          loadTickets(true);
          return;
        }
        if (updated?.id) {
          setTickets((current) => current.some((ticket) => ticket.id === updated.id)
            ? current.map((ticket) => ticket.id === updated.id ? updated : ticket)
            : [updated, ...current]);
          return;
        }
        loadTickets(true);
      };
      window.addEventListener('plumme-support-tickets-changed', refresh);
      window.addEventListener('focus', refresh);
      return () => {
        window.removeEventListener('plumme-support-tickets-changed', refresh);
        window.removeEventListener('focus', refresh);
      };
    }, [loadTickets, removeTicket]);
    const { requestDelete, deleteDialog } = useSupportTicketDelete(removeTicket);
    const updateTicket = (updated) => setTickets((current) => current.map((ticket) => ticket.id === updated.id ? updated : ticket));
    const createTicket = async (input) => {
      const result = await window.PlummeBackend.createSupportTicket({ ...input, sourceRoute: route || 'support' });
      const ticket = result.ticket;
      setTickets((current) => [ticket, ...current.filter((entry) => entry.id !== ticket.id)]);
      if (result.message) setInitialMessagesByTicket((current) => ({ ...current, [ticket.id]: [result.message] }));
      setShowNew(false);
      setActiveId(ticket.id);
    };
    const filtered = tickets.filter((ticket) => filter === 'all' || (filter === 'open' ? supportTicketIsOpen(ticket) : !supportTicketIsOpen(ticket)));
    const active = tickets.find((ticket) => ticket.id === activeId) || null;
    if (active) return <SupportTicketDetail ticket={active} initialMessages={initialMessagesByTicket[active.id] || []} onBack={() => {
      setInitialMessagesByTicket((current) => {
        const next = { ...current };
        delete next[active.id];
        return next;
      });
      setActiveId(null);
    }} onTicketChange={updateTicket} onTicketDeleted={(ticketId) => { setActiveId(null); removeTicket(ticketId); }} />;
    return <div className="support-page">
      {deleteDialog}
      {showNew && <NewSupportTicketModal onClose={() => setShowNew(false)} onCreate={createTicket} />}
      <div className="support-page-header">
        <h2 className="t-h1">{t('support.title')}</h2>
        <div className="support-toolbar">
          {global.SupportSubNav && <global.SupportSubNav value="tickets" onChange={setSubPage} />}
          <div className="seg soft" aria-label={t('support.filter.label')}>
            {[['all', 'all'], ['open', 'open'], ['closed', 'closed']].map(([value, key]) => <button type="button" key={value} className={filter === value ? 'active' : ''} aria-pressed={filter === value} onClick={() => setFilter(value)}>{t(`support.filter.${key}`)}</button>)}
          </div>
          <Button size="sm" icon="plus" onClick={() => setShowNew(true)} data-help={t('helpTopics.support.body')} data-help-title={t('helpTopics.support.title')}>{t('support.new.action')}</Button>
        </div>
      </div>
      <Card pad={false} className="support-list-card">
        {loading ? <div className="support-state"><span className="spinner" />{t('support.loading.list')}</div>
          : error ? <div className="support-state support-state-error" role="alert"><span>{error}</span><Button size="sm" variant="secondary" onClick={() => loadTickets()}>{t('support.retry')}</Button></div>
          : filtered.length === 0 ? <EmptyState noVisual title={tickets.length === 0 ? t('support.empty.title') : t('support.empty.filterTitle')} text={t('support.empty.text')} action={tickets.length === 0 ? <Button icon="plus" onClick={() => setShowNew(true)} data-help={t('helpTopics.support.body')} data-help-title={t('helpTopics.support.title')}>{t('support.new.action')}</Button> : null} />
          : <div className="tbl-wrap support-table-wrap"><table className="tbl support-table">
            <thead><tr><th>{t('support.table.ticket')}</th><th>{t('support.table.category')}</th><th>{t('support.table.sent')}</th><th>{t('support.table.lastReply')}</th><th>{t('support.table.status')}</th><th><span className="support-sr-only">{t('support.table.actions')}</span></th></tr></thead>
            <tbody>{filtered.map((ticket) => <tr key={ticket.id} role="button" tabIndex="0" onClick={() => setActiveId(ticket.id)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setActiveId(ticket.id); } }}>
              <td><div className="support-table-subject">{ticket.subject}</div><div className="t-sm muted">{ticket.id}</div></td>
              <td>{supportCategoryLabel(ticket.category)}</td>
              <td>{supportDate(ticket.createdAt)}</td>
              <td className="support-last-reply"><div>{supportDateOrDash(ticket.lastReplyAt)}</div><div className="t-sm muted">{ticket.lastReplySide === 'admin' ? t('support.lastReply.support') : t('support.lastReply.you')}</div></td>
              <td><span className={`badge ${supportTicketIsOpen(ticket) ? 'badge-info' : 'badge-success'}`}>{supportTicketIsOpen(ticket) ? t('support.status.open') : t('support.status.closed')}</span></td>
              <td className="support-table-actions">
                <button type="button" className="btn btn-ghost btn-sm support-row-delete" aria-label={t('support.delete.rowLabel', { id: ticket.id })} title={t('support.delete.rowAction')}
                  onClick={(event) => { event.stopPropagation(); requestDelete(ticket); }}
                  onKeyDown={(event) => event.stopPropagation()}>
                  <Icon name="trash" size={15} /><span className="support-row-delete-text">{t('support.delete.rowAction')}</span>
                </button>
              </td>
            </tr>)}</tbody>
          </table></div>}
      </Card>
    </div>;
  }

  // Thin dispatch: Support has two sub-pages, and the ticket view stays as it was.
  function SupportTicketsPage(props) {
    if (props.subPage === 'articles' && global.SupportArticlesPage) return <global.SupportArticlesPage {...props} />;
    return <SupportTicketsView {...props} />;
  }

  global.SupportFAB = SupportFAB;
  global.SupportTicketsPage = SupportTicketsPage;
})(typeof window !== 'undefined' ? window : globalThis);
